import React, { useEffect, useState } from 'react';
import { ChevronRight, Users, Target, Heart, Lightbulb, Shield, Award, Sparkles, Phone, Mail, MapPin, Calendar, TrendingUp } from 'lucide-react';
import Footer from '../components/Footer';
import AboutModals from '../components/AboutModals';

const About = () => {
    const [scrollY, setScrollY] = useState(0);
    const [activeValue, setActiveValue] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState({ title: '', content: '', icon: null });

    useEffect(() => {
        const handleScroll = () => {
            setScrollY(window.scrollY);
            const elements = document.querySelectorAll('.fade-in');
            elements.forEach((el, index) => {
                const rect = el.getBoundingClientRect();
                if (rect.top >= 0 && rect.top <= window.innerHeight * 0.8) {
                    setTimeout(() => {
                        el.classList.add('animate-in');
                    }, index * 50);
                }
            });
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const coreValues = [
        {
            title: 'Empathy',
            desc: 'Building connections with heartfelt understanding and genuine care for every individual.',
            icon: Heart,
            color: 'from-pink-500 to-rose-500',
            modalContent: (
                <div>
                    <p className="mb-4">
                        At Navam Biz Solutions, empathy is the cornerstone of our approach. We prioritize understanding the unique needs of every individual and organization.
                    </p>
                    <ul className="list-disc pl-5 mb-4 text-sm sm:text-base">
                        <li>Actively listening to clients’ challenges and aspirations.</li>
                        <li>Creating inclusive environments that value every perspective.</li>
                        <li>Building lasting relationships grounded in trust and care.</li>
                    </ul>
                    <p className="font-semibold text-[#0e6d87] text-sm sm:text-base">
                        “Empathy drives connection, and connection drives success.” – Navam Team
                    </p>
                </div>
            ),
        },
        {
            title: 'Innovation',
            desc: 'Crafting bold, actionable solutions that transform challenges into opportunities.',
            icon: Lightbulb,
            color: 'from-yellow-500 to-orange-500',
            modalContent: (
                <div>
                    <p className="mb-4">
                        Innovation fuels our mission to transform HR practices. We embrace creativity to deliver solutions that are both practical and groundbreaking.
                    </p>
                    <ul className="list-disc pl-5 mb-4 text-sm sm:text-base">
                        <li>Developing cutting-edge HR tools tailored to MSMEs.</li>
                        <li>Anticipating industry trends to stay ahead of the curve.</li>
                        <li>Turning challenges into opportunities for growth.</li>
                    </ul>
                    <p className="font-semibold text-[#0e6d87] text-sm sm:text-base">
                        “Innovation is not just an idea; it’s a commitment to progress.” – Navam Team
                    </p>
                </div>
            ),
        },
        {
            title: 'Integrity',
            desc: 'Anchoring trust in every relationship through transparent and ethical practices.',
            icon: Shield,
            color: 'from-blue-500 to-indigo-500',
            modalContent: (
                <div>
                    <p className="mb-4">
                        Integrity is non-negotiable at Navam. We uphold the highest ethical standards in all interactions, ensuring trust and transparency.
                    </p>
                    <ul className="list-disc pl-5 mb-4 text-sm sm:text-base">
                        <li>Maintaining honesty in all communications.</li>
                        <li>Adhering to ethical practices in every decision.</li>
                        <li>Building trust through consistent and reliable service.</li>
                    </ul>
                    <p className="font-semibold text-[#0e6d87] text-sm sm:text-base">
                        “Integrity is the foundation of every strong partnership.” – Navam Team
                    </p>
                </div>
            ),
        },
        {
            title: 'Excellence',
            desc: 'Delivering unmatched quality consistently, exceeding expectations at every turn.',
            icon: Award,
            color: 'from-purple-500 to-violet-500',
            modalContent: (
                <div>
                    <p className="mb-4">
                        Excellence defines our work. We strive to deliver exceptional quality in every project, surpassing our clients’ expectations.
                    </p>
                    <ul className="list-disc pl-5 mb-4 text-sm sm:text-base">
                        <li>Setting high standards for service delivery.</li>
                        <li>Continuously improving our processes and offerings.</li>
                        <li>Delivering results that drive measurable impact.</li>
                    </ul>
                    <p className="font-semibold text-[#0e6d87] text-sm sm:text-base">
                        “Excellence is not a goal; it’s our standard.” – Navam Team
                    </p>
                </div>
            ),
        },
        {
            title: 'Gratitude',
            desc: 'Celebrating every step with appreciation and recognizing the value in every partnership.',
            icon: Sparkles,
            color: 'from-green-500 to-emerald-500',
            modalContent: (
                <div>
                    <p className="mb-4">
                        Gratitude shapes our culture. We cherish every opportunity to collaborate and celebrate the contributions of our partners and team.
                    </p>
                    <ul className="list-disc pl-5 mb-4 text-sm sm:text-base">
                        <li>Recognizing the efforts of our clients and team members.</li>
                        <li>Celebrating milestones, big and small, with appreciation.</li>
                        <li>Fostering a positive and thankful work environment.</li>
                    </ul>
                    <p className="font-semibold text-[#0e6d87] text-sm sm:text-base">
                        “Gratitude turns partnerships into possibilities.” – Navam Team
                    </p>
                </div>
            ),
        },
    ];

    const impactStats = [
        { number: '50+', label: 'MSMEs Supported', icon: Users },
        { number: '500+', label: 'Professionals Trained', icon: TrendingUp },
        { number: '95%', label: 'Client Satisfaction', icon: Award },
        { number: '2031', label: 'Vision Goal', icon: Target },
    ];

    const openModal = (title, content, icon) => {
        setModalContent({ title, content, icon });
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-blue-50 relative overflow-hidden">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div
                    className="absolute top-20 -left-20 w-96 h-96 bg-[#0e6d87]/5 rounded-full blur-3xl"
                    style={{ transform: `translateY(${scrollY * 0.2}px)` }}
                />
                <div
                    className="absolute bottom-20 -right-20 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"
                    style={{ transform: `translateY(${scrollY * -0.3}px)` }}
                />
            </div>

            <section className="relative py-12 sm:py-16 px-4 sm:px-6 lg:px-12">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16 sm:mb-20 fade-in">
                        <div className="inline-block mb-6">
                            <span className="px-4 py-2 bg-[#0e6d87]/10 text-[#0e6d87] rounded-full text-sm sm:text-base font-medium">
                                Transforming HR Excellence Since 2020
                            </span>
                        </div>
                        <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold text-gray-900 mb-8 leading-tight">
                            <span className="bg-gradient-to-r from-[#0e6d87] via-blue-600 to-gray-700 bg-clip-text text-transparent">
                                About Navam
                            </span>
                            <br />
                            <span className="bg-gradient-to-r from-gray-700 via-[#0e6d87] to-blue-600 bg-clip-text text-transparent">
                                Biz Solutions
                            </span>
                        </h1>
                        <p className="mt-6 sm:mt-8 max-w-4xl mx-auto text-base sm:text-xl lg:text-2xl text-gray-600 leading-relaxed">
                            Empowering careers and businesses with innovative HR strategies,
                            <span className="text-[#0e6d87] font-semibold"> strategic consulting</span>, and
                            <span className="text-[#0e6d87] font-semibold"> transformative training</span>.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 mb-20 sm:mb-24">
                        <div className="group bg-white/80 backdrop-blur-sm rounded-2xl p-6 sm:p-8 lg:p-12 shadow-xl hover:shadow-2xl transition-all duration-500 border border-white/20 fade-in">
                            <div className="flex items-center mb-6">
                                <div className="w-10 sm:w-12 h-10 sm:h-12 bg-gradient-to-r from-[#0e6d87] to-blue-600 rounded-xl flex items-center justify-center mr-3 sm:mr-4">
                                    <Target className="w-5 sm:w-6 h-5 sm:h-6 text-white" />
                                </div>
                                <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Our Vision</h2>
                            </div>
                            <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed mb-6">
                                To establish HR excellence in <span className="font-bold text-[#0e6d87]">1000 MSMEs by 2031</span>,
                                fostering sustainable growth and innovation across India's entrepreneurial landscape.
                            </p>
                            <button
                                onClick={() =>
                                    openModal(
                                        'Our Vision',
                                        <div>
                                            <p className="mb-4 text-sm sm:text-base">
                                                Our vision is to revolutionize HR for 1000 MSMEs by 2031, creating a thriving ecosystem of innovation and growth across India.
                                            </p>
                                            <ul className="list-disc pl-5 mb-4 text-sm sm:text-base">
                                                <li>Empowering small businesses with world-class HR frameworks.</li>
                                                <li>Driving economic growth through job creation and skill development.</li>
                                                <li>Fostering a culture of innovation in India’s entrepreneurial landscape.</li>
                                            </ul>
                                            <p className="font-semibold text-[#0e6d87] text-sm sm:text-base">
                                                Join us in shaping the future of HR excellence.
                                            </p>
                                        </div>,
                                        Target
                                    )
                                }
                                className="flex items-center text-[#0e6d87] font-medium text-sm sm:text-base group-hover:translate-x-2 transition-transform duration-300"
                            >
                                <span>Learn more about our journey</span>
                                <ChevronRight className="w-4 h-4 ml-2" />
                            </button>
                        </div>

                        <div className="group bg-gradient-to-br from-[#0e6d87]/5 to-blue-50 rounded-2xl p-6 sm:p-8 lg:p-12 shadow-xl hover:shadow-2xl transition-all duration-500 border border-[#0e6d87]/10 fade-in">
                            <div className="flex items-center mb-6">
                                <div className="w-10 sm:w-12 h-10 sm:h-12 bg-gradient-to-r from-blue-600 to-[#0e6d87] rounded-xl flex items-center justify-center mr-3 sm:mr-4">
                                    <Users className="w-5 sm:w-6 h-5 sm:h-6 text-white" />
                                </div>
                                <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Our Purpose</h2>
                            </div>
                            <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed mb-6">
                                Revolutionizing HR with tailored solutions, empowering job seekers and businesses
                                to thrive together in an ever-evolving marketplace.
                            </p>
                            <button
                                onClick={() =>
                                    openModal(
                                        'Our Purpose',
                                        <div>
                                            <p className="mb-4 text-sm sm:text-base">
                                                Our purpose is to bridge the gap between talent and opportunity, creating synergies that empower both individuals and organizations.
                                            </p>
                                            <ul className="list-disc pl-5 mb-4 text-sm sm:text-base">
                                                <li>Providing tailored HR solutions for diverse business needs.</li>
                                                <li>Equipping job seekers with skills for a dynamic marketplace.</li>
                                                <li>Building partnerships that drive mutual success.</li>
                                            </ul>
                                            <p className="font-semibold text-[#0e6d87] text-sm sm:text-base">
                                                Let’s create a thriving future together.
                                            </p>
                                        </div>,
                                        Users
                                    )
                                }
                                className="flex items-center text-[#0e6d87] font-medium text-sm sm:text-base group-hover:translate-x-2 transition-transform duration-300"
                            >
                                <span>Discover our approach</span>
                                <ChevronRight className="w-4 h-4 ml-2" />
                            </button>
                        </div>
                    </div>

                    <div className="mb-20 sm:mb-24 fade-in">
                        <div className="text-center mb-12 sm:mb-16">
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                                Our Core Values
                            </h2>
                            <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-2xl mx-auto">
                                The principles that guide every decision and drive our commitment to excellence
                            </p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                            {coreValues.map((value, index) => {
                                const IconComponent = value.icon;
                                return (
                                    <div
                                        key={index}
                                        className={`group relative bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer border border-gray-100 fade-in ${index === 2 ? 'lg:col-start-2 lg:col-end-3' : ''
                                            }`}
                                        style={{
                                            animationDelay: `${index * 150}ms`,
                                            transform: index % 2 === 0 ? 'translateY(0)' : 'translateY(-12px)',
                                        }}
                                        onMouseEnter={() => setActiveValue(index)}
                                        onMouseLeave={() => setActiveValue(null)}
                                        onClick={() => openModal(value.title, value.modalContent, value.icon)}
                                    >
                                        <div className="relative z-10">
                                            <div
                                                className={`w-12 sm:w-14 h-12 sm:h-14 bg-gradient-to-r ${value.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}
                                            >
                                                <IconComponent className="w-6 sm:w-7 h-6 sm:h-7 text-white" />
                                            </div>
                                            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-3 group-hover:text-[#0e6d87] transition-colors duration-300">
                                                {value.title}
                                            </h3>
                                            <p className="text-gray-600 text-sm sm:text-base leading-relaxed">{value.desc}</p>
                                        </div>
                                        <div
                                            className={`absolute inset-0 bg-gradient-to-r ${value.color} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-300`}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="mb-20 sm:mb-24 bg-gradient-to-r from-[#0e6d87]/5 via-blue-50/50 to-[#0e6d87]/5 rounded-3xl p-6 sm:p-8 lg:p-12 fade-in">
                        <div className="text-center mb-12">
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                                Our Impact in Numbers
                            </h2>
                            <p className="text-sm sm:text-base lg:text-lg text-gray-600">
                                Measurable results that speak to our commitment and expertise
                            </p>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mb-12">
                            {impactStats.map((stat, index) => {
                                const IconComponent = stat.icon;
                                return (
                                    <div key={index} className="text-center group">
                                        <div className="w-14 sm:w-16 h-14 sm:h-16 bg-gradient-to-r from-[#0e6d87] to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                                            <IconComponent className="w-7 sm:w-8 h-7 sm:h-8 text-white" />
                                        </div>
                                        <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#0e6d87] mb-2">
                                            {stat.number}
                                        </div>
                                        <div className="text-xs sm:text-sm lg:text-base text-gray-600 font-medium">
                                            {stat.label}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg">
                                <div className="flex items-center mb-4">
                                    <Calendar className="w-5 sm:w-6 h-5 sm:h-6 text-[#0e6d87] mr-3" />
                                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">2024 Milestone</h3>
                                </div>
                                <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                                    Successfully supported 50+ MSMEs with comprehensive HR frameworks,
                                    directly contributing to local economic growth and job creation.
                                </p>
                            </div>
                            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg">
                                <div className="flex items-center mb-4">
                                    <Target className="w-5 sm:w-6 h-5 sm:h-6 text-[#0e6d87] mr-3" />
                                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">2031 Vision</h3>
                                </div>
                                <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                                    Empower 1000+ MSMEs across India with world-class HR solutions,
                                    creating a ripple effect of organizational excellence.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="text-center bg-white rounded-3xl p-6 sm:p-8 lg:p-12 shadow-xl fade-in">
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-8">
                            Ready to Transform Your HR?
                        </h2>
                        <p className="text-sm sm:text-base lg:text-lg text-gray-600 mb-12 max-w-2xl mx-auto">
                            Let's discuss how we can help your organization achieve HR excellence and drive sustainable growth.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-6 sm:gap-8 mb-8 max-w-3xl mx-auto justify-items-center">
                            <div className="group text-center p-4 sm:p-6 rounded-2xl hover:bg-gray-50 transition-colors duration-300 w-full max-w-xs">
                                <div className="w-10 sm:w-12 h-10 sm:h-12 bg-gradient-to-r from-[#0e6d87] to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                                    <Phone className="w-5 sm:w-6 h-5 sm:h-6 text-white animate-pulse-slow" />
                                </div>
                                <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Call Us</h3>
                                <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">

                                    <a href="tel:+917276156055" className="text-[#0e6d87] hover:underline">
                                        +91 72761 56055
                                    </a>
                                </p>
                            </div>
                            <div className="group text-center p-4 sm:p-6 rounded-2xl hover:bg-gray-50 transition-colors duration-300 w-full max-w-xs">
                                <div className="w-10 sm:w-12 h-10 sm:h-12 bg-gradient-to-r from-[#0e6d87] to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                                    <Mail className="w-5 sm:w-6 h-5 sm:h-6 text-white animate-pulse-slow" />
                                </div>
                                <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Email Us</h3>
                                <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                                    <a href="mailto:info@navambizsolutions.com" className="text-[#0e6d87] hover:underline">
                                        info@navambizsolutions.com
                                    </a>
                                </p>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            <AboutModals
                isOpen={isModalOpen}
                onClose={closeModal}
                title={modalContent.title}
                content={modalContent.content}
                icon={modalContent.icon}
            />

            <style jsx>{`
        .fade-in {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        .animate-in {
          opacity: 1;
          transform: translateY(0);
        }
        .group:hover .group-hover\\:scale-110 {
          transform: scale(1.1);
        }
        .group:hover .group-hover\\:translate-x-2 {
          transform: translateX(8px);
        }
      `}</style>

            <Footer />
        </div>
    );
};

export default About;